import { z } from 'zod';
import { HotspotTransition } from './showcase.js';
/**
 * Chapsi Showcase phase B (docs/adr/0067-showcase-chapi.md): Chapi reads every screen, links the prototype, asks about
 * what it could not place ("Needs you") and changes links when the team asks in the editor's Chapi panel, with Undo.
 * Everything here needs both the `showcase` and `chapi` features.
 */
/** What Chapi reads on a screen. `logo` and `image` are extra kinds the linker uses ("link all the logos to Home"). */
export const ScreenElementType = z.enum(['button', 'link', 'tab', 'back', 'menu', 'card', 'input', 'logo', 'image']);
export const ScreenKind = z.enum(['page', 'modal', 'menu', 'bottom_sheet']);
/** More screens than this waiting to be read asks the person to confirm the estimate first. */
export const CHAPI_CONFIRM_OVER_SCREENS = 20;
/** Tokens one screen usually takes to read (image, instructions and the answer); the estimate's unit. */
export const CHAPI_TOKENS_PER_SCREEN = 2_500;
export const CHAPI_CHAT_MAX_CHARS = 1_000;
export const DesignChapiRunStatus = z.enum(['awaiting_confirmation', 'queued', 'running', 'done', 'failed']);
export const DesignChapiRun = z.object({
    id: z.uuid(),
    status: DesignChapiRunStatus,
    /** Screens Chapi still had to read when the run started, and how many it has read. */
    screensToRead: z.number().int(),
    screensRead: z.number().int(),
    /** Shown before a large upload is read (and while it waits for the person to confirm). */
    estimate: z.object({ screens: z.number().int(), tokens: z.number().int(), costUsd: z.number(), allowanceLeft: z.number().int() }).nullable(),
    /** Hotspots Chapi applied (high and medium confidence), on how many screens, and how many need the person. */
    linked: z.number().int(),
    linkedScreens: z.number().int(),
    needs: z.number().int(),
    /** Plain sentences about what Chapi did ("The tab bar repeats on 4 screens…"). */
    notes: z.array(z.string()),
    error: z.string().nullable(),
    finishedAt: z.string().nullable(),
});
export const ChapiSuggestion = z.object({
    targetKind: z.enum(['screen', 'overlay', 'back']),
    targetScreenId: z.uuid().nullable(),
    label: z.string(),
});
export const DesignChapiQuestion = z.object({
    id: z.uuid(),
    /** `element`: a tappable thing Chapi could not place. `unreachable`: nothing leads to this screen. */
    kind: z.enum(['element', 'unreachable']),
    screenId: z.uuid(),
    screenTitle: z.string(),
    hotspotId: z.uuid().nullable(),
    label: z.string(),
    question: z.string(),
    box: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }).nullable(),
    suggestions: z.array(ChapiSuggestion),
});
export const DesignChapiChangeItem = z.object({
    hotspotId: z.uuid(),
    screenId: z.uuid(),
    screenTitle: z.string(),
    /** "Logo → Home", "Book a visit: slide up". */
    summary: z.string(),
});
export const DesignChapiChange = z.object({
    id: z.uuid(),
    instruction: z.string(),
    reply: z.string(),
    /** `none`: Chapi answered without changing anything (a question). */
    status: z.enum(['applied', 'undone', 'none']),
    changes: z.array(DesignChapiChangeItem),
    /** The latest applied batch can be undone in one step. */
    undoable: z.boolean(),
    /** Changes left as they were on Undo because someone edited them since. */
    keptOnUndo: z.number().int(),
    createdAt: z.string(),
});
export const DesignChapiState = z.object({
    /** Chapi can work on this set: the feature is on, the server has a model for the workspace's path, the person may use it. */
    available: z.boolean(),
    /** Why not, in a sentence, when it isn't. */
    unavailableReason: z.string().nullable(),
    canEdit: z.boolean(),
    run: DesignChapiRun.nullable(),
    /** "I linked 26 hotspots across 7 screens. 3 need you." once a run has finished. */
    report: z.string().nullable(),
    questions: z.array(DesignChapiQuestion),
    /** The person's own conversation with Chapi about this set, oldest first (the last 20). */
    changes: z.array(DesignChapiChange),
});
export const RunDesignChapiInput = z.object({
    /** True once the person has seen the estimate for a large upload. */
    confirm: z.boolean().default(false),
});
export const AnswerDesignChapiQuestionInput = z.discriminatedUnion('action', [
    z.object({ action: z.literal('link'), targetKind: z.enum(['screen', 'overlay', 'back']), targetScreenId: z.uuid().nullable().optional(), transition: HotspotTransition.optional() }),
    z.object({ action: z.literal('dismiss') }),
]);
export const ChatDesignChapiInput = z.object({ text: z.string().trim().min(1, 'Tell Chapi what to change').max(CHAPI_CHAT_MAX_CHARS) });
/** "I linked 26 hotspots across 7 screens. 3 need you." */
export function chapiReport(r) {
    const hs = `${r.linked} ${r.linked === 1 ? 'hotspot' : 'hotspots'}`;
    const sc = `${r.linkedScreens} ${r.linkedScreens === 1 ? 'screen' : 'screens'}`;
    const needs = r.needs === 0 ? 'Nothing needs you.' : `${r.needs} ${r.needs === 1 ? 'needs' : 'need'} you.`;
    return `I linked ${hs} across ${sc}. ${needs}`;
}
//# sourceMappingURL=showcase-chapi.js.map